// ==========================================
// Buddy shell state — placement, size, density, prefs
// ==========================================
// The web stand-in for the desktop spec's window management (3d LAYOUT & PLACEMENT,
// 4a). Everything the user can rearrange persists to localStorage, which is already
// this app's habit (see MainLayoutComponent).
// ==========================================

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import {
  BubbleCorner,
  BuddyLayout,
  BuddyPlacement,
  BuddySurface,
  Density,
  PanelEdge,
} from '../models/buddy.types';
import {
  DENSITY_BREAKPOINTS,
  RAIL_DEFAULT_HEIGHT,
  RAIL_DEFAULT_WIDTH,
  PANEL_DEFAULT_HEIGHT,
  PANEL_DEFAULT_WIDTH,
  PANEL_MAX_HEIGHT,
  PANEL_MAX_WIDTH,
  PANEL_MIN_HEIGHT,
  PANEL_MIN_WIDTH,
  STORAGE_KEY_PLACEMENT,
  STORAGE_KEY_PREFS,
} from '../models/buddy.constants';

interface BuddyPrefs {
  tts: boolean;
  /** Collapsed Buddy can sit on the edge tab instead of the corner bubble (4a). */
  useEdgeTab: boolean;
  /** 3a stacked panel, or 1c's quest-rail sidebar. */
  layout: BuddyLayout;
  /**
   * The rail's quest block is "dismissible for the day" (1d). Stores the date it
   * was dismissed on, so it comes back tomorrow on its own.
   */
  questDismissedOn: string;
}

const DEFAULT_PLACEMENT: BuddyPlacement = {
  edge: 'right',
  corner: 'bottom-right',
  width: PANEL_DEFAULT_WIDTH,
  height: PANEL_DEFAULT_HEIGHT,
};

const DEFAULT_PREFS: BuddyPrefs = {
  tts: true,
  useEdgeTab: false,
  layout: 'panel',
  questDismissedOn: '',
};

/** Each layout opens at its own natural size (3a 340x620, 1c 300x480). */
export const LAYOUT_DEFAULT_SIZE: Readonly<Record<BuddyLayout, { width: number; height: number }>> = {
  panel: { width: PANEL_DEFAULT_WIDTH, height: PANEL_DEFAULT_HEIGHT },
  rail: { width: RAIL_DEFAULT_WIDTH, height: RAIL_DEFAULT_HEIGHT },
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

@Injectable({ providedIn: 'root' })
export class BuddyStateService {
  private readonly placementSubject = new BehaviorSubject<BuddyPlacement>(this.readPlacement());
  private readonly prefsSubject = new BehaviorSubject<BuddyPrefs>(this.readPrefs());
  private readonly surfaceSubject = new BehaviorSubject<BuddySurface>('bubble');
  private readonly habitatSubject = new BehaviorSubject<boolean>(false);
  private readonly paletteSubject = new BehaviorSubject<boolean>(false);
  private readonly historySubject = new BehaviorSubject<boolean>(false);

  readonly placement: Observable<BuddyPlacement> = this.placementSubject.asObservable();
  readonly surface: Observable<BuddySurface> = this.surfaceSubject.pipe(distinctUntilChanged());
  readonly habitatOpen: Observable<boolean> = this.habitatSubject.pipe(distinctUntilChanged());
  readonly paletteOpen: Observable<boolean> = this.paletteSubject.pipe(distinctUntilChanged());
  readonly historyOpen: Observable<boolean> = this.historySubject.pipe(distinctUntilChanged());

  /** 3c responsive ladder, derived from the panel's current width. */
  readonly density: Observable<Density> = this.placementSubject.pipe(
    map((p) => BuddyStateService.densityFor(p.width)),
    distinctUntilChanged()
  );

  readonly ttsEnabled: Observable<boolean> = this.prefsSubject.pipe(
    map((p) => p.tts),
    distinctUntilChanged()
  );

  readonly useEdgeTab: Observable<boolean> = this.prefsSubject.pipe(
    map((p) => p.useEdgeTab),
    distinctUntilChanged()
  );

  readonly layout: Observable<BuddyLayout> = this.prefsSubject.pipe(
    map((p) => p.layout),
    distinctUntilChanged()
  );

  /** False once the day's quest has been dismissed; resets itself the next day. */
  readonly questVisible: Observable<boolean> = this.prefsSubject.pipe(
    map((p) => p.questDismissedOn !== BuddyStateService.today()),
    distinctUntilChanged()
  );

  static densityFor(width: number): Density {
    for (const bp of DENSITY_BREAKPOINTS) {
      if (width <= bp.max) {
        return bp.density;
      }
    }
    return 'comfortable';
  }

  get currentPlacement(): BuddyPlacement {
    return this.placementSubject.value;
  }

  get currentSurface(): BuddySurface {
    return this.surfaceSubject.value;
  }

  get currentDensity(): Density {
    return BuddyStateService.densityFor(this.placementSubject.value.width);
  }

  get isTtsEnabled(): boolean {
    return this.prefsSubject.value.tts;
  }

  get currentLayout(): BuddyLayout {
    return this.prefsSubject.value.layout;
  }

  private static today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  get isPanelOpen(): boolean {
    return this.surfaceSubject.value === 'panel';
  }

  /** Honours the OS setting; every Buddy animation checks this before running. */
  get prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  }

  // ------------------------------------------
  // Open / close
  // ------------------------------------------

  open(): void {
    this.surfaceSubject.next('panel');
  }

  close(): void {
    this.surfaceSubject.next('bubble');
    this.habitatSubject.next(false);
    this.paletteSubject.next(false);
    this.historySubject.next(false);
  }

  toggle(): void {
    if (this.isPanelOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  openHabitat(): void {
    this.habitatSubject.next(true);
  }

  closeHabitat(): void {
    this.habitatSubject.next(false);
  }

  setPaletteOpen(open: boolean): void {
    this.paletteSubject.next(open);
  }

  togglePalette(): void {
    this.paletteSubject.next(!this.paletteSubject.value);
  }

  // ------------------------------------------
  // Chat history drawer (Cmd/Ctrl+J)
  // ------------------------------------------

  setHistoryOpen(open: boolean): void {
    this.historySubject.next(open);
    if (open) {
      // Only one overlay at a time inside the panel.
      this.paletteSubject.next(false);
      this.habitatSubject.next(false);
    }
  }

  toggleHistory(): void {
    this.setHistoryOpen(!this.historySubject.value);
  }

  // ------------------------------------------
  // Placement
  // ------------------------------------------

  setEdge(edge: PanelEdge): void {
    this.patchPlacement({ edge });
  }

  setCorner(corner: BubbleCorner): void {
    // Keep the panel on the same side the bubble was dragged to.
    const edge: PanelEdge = corner.endsWith('left') ? 'left' : 'right';
    this.patchPlacement({ corner, edge });
  }

  /** Resize is clamped to the spec's 260-420 x 400-900 range. */
  setSize(width: number, height: number): void {
    this.patchPlacement({
      width: Math.round(clamp(width, PANEL_MIN_WIDTH, PANEL_MAX_WIDTH)),
      height: Math.round(clamp(height, PANEL_MIN_HEIGHT, PANEL_MAX_HEIGHT)),
    });
  }

  // ------------------------------------------
  // Prefs
  // ------------------------------------------

  setTts(on: boolean): void {
    this.patchPrefs({ tts: on });
  }

  toggleTts(): boolean {
    const next = !this.prefsSubject.value.tts;
    this.patchPrefs({ tts: next });
    return next;
  }

  setUseEdgeTab(on: boolean): void {
    this.patchPrefs({ useEdgeTab: on });
  }

  /**
   * 4a draws the corner bubble and the edge tab side by side and says "ship one".
   * Both are built, so this is the switch between them.
   */
  toggleCollapseStyle(): boolean {
    const next = !this.prefsSubject.value.useEdgeTab;
    this.patchPrefs({ useEdgeTab: next });
    return next;
  }

  /** Switching layout also resizes to that layout's natural dimensions. */
  setLayout(layout: BuddyLayout): void {
    if (this.prefsSubject.value.layout === layout) {
      return;
    }
    this.patchPrefs({ layout });
    const size = LAYOUT_DEFAULT_SIZE[layout];
    this.setSize(size.width, size.height);
  }

  toggleLayout(): BuddyLayout {
    const next: BuddyLayout = this.currentLayout === 'panel' ? 'rail' : 'panel';
    this.setLayout(next);
    return next;
  }

  /** 1d: the sidebar's quest line is dismissible for the day. */
  dismissQuestForToday(): void {
    this.patchPrefs({ questDismissedOn: BuddyStateService.today() });
  }

  // ------------------------------------------
  // Persistence
  // ------------------------------------------

  private patchPlacement(patch: Partial<BuddyPlacement>): void {
    const next: BuddyPlacement = { ...this.placementSubject.value, ...patch };
    this.placementSubject.next(next);
    this.write(STORAGE_KEY_PLACEMENT, next);
  }

  private patchPrefs(patch: Partial<BuddyPrefs>): void {
    const next: BuddyPrefs = { ...this.prefsSubject.value, ...patch };
    this.prefsSubject.next(next);
    this.write(STORAGE_KEY_PREFS, next);
  }

  private readPlacement(): BuddyPlacement {
    const stored = this.read<Partial<BuddyPlacement>>(STORAGE_KEY_PLACEMENT);
    if (!stored) {
      return { ...DEFAULT_PLACEMENT };
    }
    return {
      edge: stored.edge === 'left' ? 'left' : 'right',
      corner: stored.corner ?? DEFAULT_PLACEMENT.corner,
      width: clamp(stored.width ?? PANEL_DEFAULT_WIDTH, PANEL_MIN_WIDTH, PANEL_MAX_WIDTH),
      height: clamp(stored.height ?? PANEL_DEFAULT_HEIGHT, PANEL_MIN_HEIGHT, PANEL_MAX_HEIGHT),
    };
  }

  private readPrefs(): BuddyPrefs {
    const stored = this.read<Partial<BuddyPrefs>>(STORAGE_KEY_PREFS);
    if (!stored) {
      return { ...DEFAULT_PREFS };
    }
    return {
      tts: stored.tts ?? DEFAULT_PREFS.tts,
      useEdgeTab: stored.useEdgeTab ?? DEFAULT_PREFS.useEdgeTab,
      layout: stored.layout === 'rail' ? 'rail' : 'panel',
      questDismissedOn: stored.questDismissedOn ?? '',
    };
  }

  private read<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage can be unavailable (private mode, quota). Placement is not worth failing over.
    }
  }
}
